import { Combobox, Input, useCombobox } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Authorizations } from "~/api/types";

import { Keyword } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import Button from "../mantine/button/Button";
import Spinner from "../ui/spinner/Spinner";
import BulkUpdateModalContent from "./BulkUpdateModalContent";
import styles from "./KeywordTableActions.module.scss";

interface BulkDeletePayload {
  ids: number[];
}

interface BulkUpdatePayload {
  ids: number[];
  attributes: KeywordAttributes;
}

interface KeywordAttributes {
  refresh_frequency_override?: number;
  location_id: number;
  country: string;
  language: string;
  ranking_url: string;
}

interface KeywordTableActionsProps {
  selectedItems?: Keyword[];
  selectedItemsDelete: Keyword[];
  selectedItemsRefresh: Keyword[];
  setSelectedItemsDelete: (items: Keyword[]) => void;
  setSelectedItemsRefresh: (items: Keyword[]) => void;
  authorizations?: Authorizations;
  setRowSelection: (value: RowSelectionState) => void;
  refetch: () => void;
}

const KeywordTableActions = ({
  selectedItems = [],
  selectedItemsDelete,
  selectedItemsRefresh,
  setSelectedItemsDelete,
  setRowSelection,
  setSelectedItemsRefresh,
  refetch,
}: KeywordTableActionsProps) => {
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => setIsOpen(false),
    onDropdownOpen: () => setIsOpen(true),
  });

  const toggleDropdown = () => {
    if (combobox) {
      combobox.toggleDropdown();
    }
  };

  const { mutate: refreshBulk, isPending: isPendingRefreshBulk } = useMutation({
    mutationFn: async (data: BulkUpdatePayload) => {
      if (!id) throw new Error("Site ID is required");
      const response = await apiClient.sites.keywordsBulkPartialUpdate(
        Number(id),
        // @ts-expect-error BE types
        data,
      );

      return response.data;
    },
    onSuccess: () => {
      handleBulkSuccess();
    },
    onError: (error: {
      error?: { error?: { message?: string; code?: string } };
    }) => {
      const errorMessage = error?.error?.error?.message
        ? `${error.error.error.message} Error Code: ${error.error.error.code || "unknown"}`
        : "An unknown error occurred";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
      combobox.resetSelectedOption();
      combobox.closeDropdown();
    },
  });

  const { mutate: bulkDelete, isPending: isPendingBulkDelete } = useMutation({
    mutationFn: async (data: BulkDeletePayload) => {
      if (!id) throw new Error("Site ID is required");
      const response = await apiClient.sites.keywordsBulkDelete(
        Number(id),
        data,
      );

      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar(`Keywords were successfully deleted`, {
        variant: "success",
      });
      refetch();
      combobox.resetSelectedOption();
      combobox.closeDropdown();
      setSelectedItemsDelete([]);
      setRowSelection({});
    },
    onError: (error: {
      error?: { error?: { message?: string; code?: string } };
    }) => {
      const errorMessage = error?.error?.error?.message
        ? `${error.error.error.message} Error Code: ${error.error.error.code || "unknown"}`
        : "An unknown error occurred";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
      combobox.resetSelectedOption();
      combobox.closeDropdown();
    },
  });

  const handleBulkSuccess = () => {
    refetch();

    setOpen(false);
    setSelectedItemsRefresh([]);
    setRowSelection({});
    enqueueSnackbar(`Keywords were successfully updated`, {
      variant: "success",
    });
  };

  if (!combobox) {
    return null;
  }

  return (
    <>
      <Combobox store={combobox} withinPortal={false}>
        <Combobox.Target>
          <Button
            component="button"
            type="button"
            variant="outlined"
            onClick={toggleDropdown}
            style={{ textAlign: "left" }}
            rightSection={<Icon iconName={isOpen ? "IconUp" : "IconDown"} />}
          >
            <Input.Placeholder>
              {selectedItems?.length > 0
                ? `Keywords Selected (${selectedItems.length})`
                : "Keyword Actions"}
            </Input.Placeholder>
          </Button>
        </Combobox.Target>
        <Combobox.Dropdown className={styles.selectFilterDropdown}>
          <Combobox.Options>
            <>
              <Combobox.Option value="bulkRecrawl">
                <Button
                  variant="menuItem"
                  disabled={!selectedItemsRefresh?.length}
                  onClick={() => {
                    setOpen(true);
                    combobox.closeDropdown();
                  }}
                >
                  <div className={styles.flexTitle}>
                    Updated Selected ({selectedItemsRefresh?.length || 0})
                  </div>
                </Button>
              </Combobox.Option>
              <Combobox.Option value="bulkIgnore">
                <Button
                  variant="menuItem"
                  disabled={!selectedItemsDelete?.length}
                  onClick={() => {
                    const selectedIds = selectedItemsDelete.map(
                      (item) => item.id,
                    );
                    bulkDelete({ ids: selectedIds });
                  }}
                >
                  <div className={styles.flexTitle}>
                    {isPendingBulkDelete && <Spinner size="1rem" />}
                    Delete Selected ({selectedItemsDelete?.length || 0})
                  </div>
                </Button>
              </Combobox.Option>
            </>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {open && (
        <BulkUpdateModalContent
          isPendingRefreshBulk={isPendingRefreshBulk}
          isOpen={open}
          closeModal={() => setOpen(false)}
          //@ts-expect-error be type
          setKeywordAttributes={(attributes: KeywordAttributes) => {
            const selectedIds = selectedItemsRefresh.map((item) => item.id);
            refreshBulk({
              ids: selectedIds,
              attributes: attributes,
            });
          }}
        />
      )}
    </>
  );
};

export default KeywordTableActions;
